import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, LockClosedIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import Input from '../../components/common/Input';
import Spinner from '../../components/common/Spinner';
import ManualPaymentScheduleEditor from '../../components/contracts/ManualPaymentScheduleEditor';
import PaymentSchedulePreview from '../../components/contracts/PaymentSchedulePreview';
import ContractPreviewModal from '../../components/contracts/ContractPreviewModal';
import { useUIStore } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import { calculatePayments } from '../../utils/paymentCalculator';
import { getSeasonDates } from '../../utils/seasonUtils';
import * as contractTemplatesApi from '../../api/contractTemplates';
import * as contractBuilderApi from '../../api/contractBuilder';
import * as trainersApi from '../../api/trainers';
import * as teamsApi from '../../api/teams';
import * as userSignaturesApi from '../../api/userSignatures';
import { settingsApi } from '../../api/settings';

// Field categorization constants
const AUTO_FILLED_FIELDS = [
  'creation_date', 'tc_name', 'tc_digital_sign',
  'season_start', 'season_end', 'month_start', 'month_end',
  'total_amount', 'installments', 'payment_schedule',
  'birth_date', 'location', 'sign_date' // Unnecessary fields
];

const TRAINER_SIGNATURE_FIELDS = [
  'trainer_signature', 'trainer_digital_sign'
];

const CREATOR_SIGNATURE_FIELDS = [
  'tc_user_signature'
];

const HIDDEN_FIELDS = [
  ...AUTO_FILLED_FIELDS,
  ...TRAINER_SIGNATURE_FIELDS,
  ...CREATOR_SIGNATURE_FIELDS
];

/**
 * Contract Builder Page
 * Unified contract generation with template variables and payment scheduling
 */
export default function ContractBuilder() {
  const navigate = useNavigate();
  const { showToast } = useUIStore();
  const { user } = useAuthStore();

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [template, setTemplate] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [hasUserSignature, setHasUserSignature] = useState(false);
  
  // Template variables
  const [variables, setVariables] = useState({});
  const [autoFilledFields, setAutoFilledFields] = useState(new Set());
  
  // Preview modal
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  
  // Contract metadata
  const [formData, setFormData] = useState({
    title: '',
    start_date: '',
    end_date: '',
    total_amount: '',
    payment_frequency: 'twice-yearly',
    payment_day_of_month: '25',
  });
  
  // Payment schedule
  const [manualPayments, setManualPayments] = useState([]);
  
  // Selections
  const [selectedTrainerId, setSelectedTrainerId] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');

  useEffect(() => {
    loadData();
  }, []);
  
  // Auto-update payment variables when payment data changes
  useEffect(() => {
    updatePaymentVariables();
  }, [formData.total_amount, formData.start_date, formData.end_date, formData.payment_frequency, formData.payment_day_of_month, manualPayments]);
  
  // Load volleyball season settings and auto-populate dates
  useEffect(() => {
    const loadSeasonDates = async () => {
      try {
        const response = await settingsApi.getByCategory('application');
        const volleyballSeason = response.data?.volleyball_season;
        
        if (volleyballSeason) {
          const seasonDates = getSeasonDates(volleyballSeason);
          if (seasonDates) {
            setFormData(prev => ({
              ...prev,
              start_date: seasonDates.start_date,
              end_date: seasonDates.end_date
            }));
          }
        }
      } catch (error) {
        // Silently fail if settings not available
        console.warn('Could not load volleyball season settings:', error);
      }
    };
    
    loadSeasonDates();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load active template
      const templateData = await contractTemplatesApi.getActiveTemplate();
      if (!templateData) {
        showToast('No active template found. Please set an active template first.', 'error');
        navigate('/templates');
        return;
      }
      
      setTemplate(templateData);

      // Load trainers, teams, and user signature
      const [trainersRes, teamsRes, userSignatureRes] = await Promise.all([
        trainersApi.getTrainers({ limit: 1000 }),
        teamsApi.getTeams({ limit: 1000 }),
        userSignaturesApi.getMySignature().catch(() => null), // Don't fail if no signature
      ]);

      setTrainers(trainersRes.data || []);
      setTeams(teamsRes.data || []);
      
      const userSignature = userSignatureRes?.signature_blob || null;
      setHasUserSignature(!!userSignature);
      console.log('[ContractBuilder DEBUG] Loaded user signature:', userSignature ? 'Present' : 'Missing');
      
      // Show warning if user has no signature
      if (!userSignature) {
        showToast(
          'Warning: You have not set up your signature. Please add your signature in Profile Settings before generating contracts.',
          'warning'
        );
      }

      // Initialize variables with empty values
      const initialVars = {};
      (templateData.template_variables || []).forEach(v => {
        initialVars[v.name] = '';
      });
      
      // Debug: Log template variables
      console.log('[ContractBuilder DEBUG] Template variables:', templateData.template_variables);
      console.log('[ContractBuilder DEBUG] Template variable names:', templateData.template_variables?.map(v => v.name));
      
      // Auto-fill creation_date and tc_name immediately
      const todayISO = new Date().toISOString().split('T')[0];
      initialVars.creation_date = todayISO;
      
      // Debug: Log user object
      console.log('[ContractBuilder DEBUG] User object:', user);
      console.log('[ContractBuilder DEBUG] User first_name:', user?.first_name);
      console.log('[ContractBuilder DEBUG] User last_name:', user?.last_name);
      
      if (user?.first_name && user?.last_name) {
        initialVars.tc_name = `${user.first_name} ${user.last_name}`;
        console.log('[ContractBuilder DEBUG] tc_name set to:', initialVars.tc_name);
      } else {
        console.log('[ContractBuilder DEBUG] User first_name or last_name is missing');
      }
      
      // Auto-fill tc_digital_sign
      const currentDate = new Date().toLocaleDateString('nl-NL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
      initialVars.tc_digital_sign = `Digitaal ondertekend door ondergetekende op ${currentDate} vanaf IP: (will be set on generation)`;
      
      // Auto-fill tc_user_signature if user has a signature
      if (userSignature) {
        initialVars.tc_user_signature = userSignature;
        console.log('[ContractBuilder DEBUG] tc_user_signature set from loaded signature');
      } else {
        console.log('[ContractBuilder DEBUG] User has no signature - field will be empty');
      }
      
      console.log('[ContractBuilder DEBUG] All initialVars:', initialVars);
      
      setVariables(initialVars);
      
      // Mark auto-filled fields
      const autoFillSet = new Set(['creation_date', 'tc_digital_sign']);
      if (user?.first_name && user?.last_name) {
        autoFillSet.add('tc_name');
      }
      if (userSignature) {
        autoFillSet.add('tc_user_signature');
      }
      setAutoFilledFields(autoFillSet);
    } catch (error) {
      showToast('Failed to load data', 'error');
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAutoFill = async () => {
    if (!selectedTrainerId || !selectedTeamId) {
      showToast('Please select a trainer and team first', 'error');
      return;
    }

    try {
      // Pass contract dates to backend for season parameter calculation
      const params = {
        trainer_id: selectedTrainerId,
        team_id: selectedTeamId,
      };
      
      // Include dates if available for season calculation
      if (formData.start_date) params.start_date = formData.start_date;
      if (formData.end_date) params.end_date = formData.end_date;
      
      const response = await contractBuilderApi.prepareTemplate(template.id, params);

      // Populate template variables - handle both direct data and nested response
      const populatedVars = response.populated_variables || response.data?.populated_variables || {};
      
      // Convert NL date format (dd-MM-yyyy) to ISO format (yyyy-MM-dd) for date fields
      const convertedVars = {};
      Object.keys(populatedVars).forEach(key => {
        const value = populatedVars[key];
        
        // Check if value matches NL date format (dd-MM-yyyy)
        if (typeof value === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(value)) {
          const [day, month, year] = value.split('-');
          convertedVars[key] = `${year}-${month}-${day}`; // Convert to ISO format
        } else {
          convertedVars[key] = value;
        }
      });
      
      // Preserve tc_name and creation_date that were set on page load
      setVariables(prev => ({
        ...prev,
        ...convertedVars,
        // Re-apply tc_name if it was set initially but not in backend response
        tc_name: convertedVars.tc_name || prev.tc_name,
        creation_date: convertedVars.creation_date || prev.creation_date
      }));
      
      // Track which fields were auto-filled
      const autoFilled = new Set(Object.keys(convertedVars).filter(key => convertedVars[key]));
      // Keep tc_name and creation_date as auto-filled
      autoFilled.add('tc_name');
      autoFilled.add('creation_date');
      
      // Also track season/month fields if they exist in convertedVars
      if (convertedVars.season_start) autoFilled.add('season_start');
      if (convertedVars.season_end) autoFilled.add('season_end');
      if (convertedVars.month_start) autoFilled.add('month_start');
      if (convertedVars.month_end) autoFilled.add('month_end');
      
      setAutoFilledFields(autoFilled);
      
      // Auto-populate contract metadata from trainer/team
      const trainer = trainers.find(t => t.id === parseInt(selectedTrainerId));
      const team = teams.find(t => t.id === parseInt(selectedTeamId));
      
      if (trainer && team) {
        setFormData(prev => ({
          ...prev,
          title: `${trainer.first_name} ${trainer.last_name} - ${team.team_name} Contract`,
        }));
      }
      
      showToast('Form auto-filled successfully', 'success');
    } catch (error) {
      showToast('Failed to auto-fill form', 'error');
      console.error('Error auto-filling:', error);
    }
  };

  const handleVariableChange = (name, value) => {
    setVariables(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // If manually edited, remove from auto-filled set
    setAutoFilledFields(prev => {
      const newSet = new Set(prev);
      newSet.delete(name);
      return newSet;
    });
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Auto-populate season/month fields from dates
    if (name === 'start_date' || name === 'end_date') {
      const startDate = name === 'start_date' ? value : formData.start_date;
      const endDate = name === 'end_date' ? value : formData.end_date;
      
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        // Extract year and month info
        const seasonStart = start.getFullYear();
        const seasonEnd = end.getFullYear();
        
        const monthNames = ['januari', 'februari', 'maart', 'april', 'mei', 'juni',
                           'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
        const monthStart = monthNames[start.getMonth()];
        const monthEnd = monthNames[end.getMonth()];
        
        // Update template variables with this info
        setVariables(prev => ({
          ...prev,
          season_start: seasonStart.toString(),
          season_end: seasonEnd.toString(),
          month_start: monthStart,
          month_end: monthEnd,
        }));
        
        // Mark these as auto-filled
        setAutoFilledFields(prev => {
          const newSet = new Set(prev);
          newSet.add('season_start');
          newSet.add('season_end');
          newSet.add('month_start');
          newSet.add('month_end');
          return newSet;
        });
      }
    }
    
    // Auto-populate payment-related fields from Step 2 & 3
    updatePaymentVariables();
  };
  
  // Helper function to format currency in Dutch notation (€X.XXX,XX)
  const formatDutchCurrency = (amount) => {
    const rounded = Math.round(amount * 100) / 100;
    const [integer, decimal] = rounded.toFixed(2).split('.');
    
    // Add thousand separators (period in Dutch)
    const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    
    // Use comma for decimal separator
    return `€${formattedInteger},${decimal}`;
  };
  
  // Helper function to format payment schedule as HTML table
  const formatPaymentScheduleAsTable = (payments, totalAmount) => {
    if (!payments || payments.length === 0) {
      return '';
    }
    
    const monthNames = ['januari', 'februari', 'maart', 'april', 'mei', 'juni',
                       'juli', 'augustus', 'september', 'oktober', 'november', 'december'];
    
    // Table styles (inline for PDF compatibility)
    const tableStyle = 'width: 100%; border-collapse: collapse; margin: 1em 0; font-size: 14px;';
    const thStyle = 'border: 1px solid #d1d5db; padding: 8px; text-align: left; background-color: #f3f4f6; font-weight: bold;';
    const tdStyle = 'border: 1px solid #d1d5db; padding: 8px;';
    const tdRightStyle = 'border: 1px solid #d1d5db; padding: 8px; text-align: right;';
    const tfootStyle = 'background-color: #f3f4f6; font-weight: bold;';
    
    // Generate table rows
    const rows = payments.map(payment => {
      const date = new Date(payment.scheduled_date);
      const day = date.getDate();
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      const formattedDate = `${day} ${month} ${year}`;
      const formattedAmount = formatDutchCurrency(payment.amount);
      
      return `
        <tr>
          <td style="${tdStyle}">${payment.payment_number}</td>
          <td style="${tdStyle}">${formattedDate}</td>
          <td style="${tdRightStyle}">${formattedAmount}</td>
        </tr>`;
    }).join('');
    
    // Calculate total (use provided totalAmount for accuracy)
    const formattedTotal = formatDutchCurrency(totalAmount);
    
    // Build complete table
    return `
<table style="${tableStyle}">
  <thead>
    <tr>
      <th style="${thStyle}">#</th>
      <th style="${thStyle}">Datum</th>
      <th style="${thStyle}; text-align: right;">Bedrag</th>
    </tr>
  </thead>
  <tbody>${rows}
  </tbody>
  <tfoot>
    <tr style="${tfootStyle}">
      <td colspan="2" style="${tdStyle}">Totaal</td>
      <td style="${tdRightStyle}">${formattedTotal}</td>
    </tr>
  </tfoot>
</table>`.trim();
  };
  
  // Helper function to update payment-related template variables
  const updatePaymentVariables = () => {
    if (!formData.total_amount || !formData.start_date || !formData.end_date) {
      return;
    }
    
    const payments = formData.payment_frequency === 'manual'
      ? manualPayments
      : calculatePayments(formData);
    
    if (payments.length === 0) {
      return;
    }
    
    // Generate HTML table for payment schedule
    const paymentScheduleTable = formatPaymentScheduleAsTable(
      payments,
      parseFloat(formData.total_amount)
    );
    
    // Update template variables
    setVariables(prev => ({
      ...prev,
      total_amount: formData.total_amount,
      installments: payments.length.toString(),
      payment_schedule: paymentScheduleTable,
    }));
    
    // Mark as auto-filled
    setAutoFilledFields(prev => {
      const newSet = new Set(prev);
      newSet.add('total_amount');
      newSet.add('installments');
      newSet.add('payment_schedule');
      return newSet;
    });
  };

  const handlePreview = async () => {
    try {
      const response = await contractBuilderApi.previewContract({
        template_id: template.id,
        variables,
      });

      // Show preview in modal
      // Response is already the data object, not wrapped in .data
      setPreviewHtml(response.html || response.data?.html);
      setShowPreviewModal(true);
    } catch (error) {
      showToast('Failed to generate preview', 'error');
      console.error('Error generating preview:', error);
    }
  };

  const handleGenerate = async () => {
    // 1. Validate contract metadata
    if (!formData.title || !formData.start_date || !formData.end_date || !formData.total_amount) {
      showToast('Please fill in all contract details (title, dates, amount)', 'error');
      return;
    }
    
    // 2. CRITICAL: Check creator signature FIRST
    if (!variables.tc_user_signature || !variables.tc_user_signature.trim()) {
      showToast(
        'You must set up your signature in your profile before generating contracts. Go to Settings → User Signatures.',
        'error'
      );
      return;
    }
    
    // 3. Validate user-editable fields only (exclude all hidden fields)
    const requiredVars = (template.template_variables || [])
      .filter(v => v.required && !HIDDEN_FIELDS.includes(v.name));
    const missingVars = requiredVars.filter(v => !variables[v.name]?.trim());

    if (missingVars.length > 0) {
      // Enhanced error message with guidance
      const missingFieldNames = missingVars.map(v => v.label).join(', ');
      const canAutoFill = selectedTrainerId && selectedTeamId;
      
      if (canAutoFill) {
        showToast(
          `Missing required fields: ${missingFieldNames}. Try using the Auto-fill button in Step 1, or fill them manually in Step 4.`,
          'error'
        );
      } else {
        showToast(
          `Missing required fields: ${missingFieldNames}. Please select a trainer and team in Step 1 and use Auto-fill, or fill the fields manually in Step 4.`,
          'error'
        );
      }
      return;
    }
    
    // Validate manual payments if applicable
    if (formData.payment_frequency === 'manual') {
      if (manualPayments.length === 0) {
        showToast('Please add at least one payment', 'error');
        return;
      }
      
      const totalPaymentAmount = manualPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
      const contractAmount = parseFloat(formData.total_amount);
      if (Math.abs(totalPaymentAmount - contractAmount) > 0.01) {
        showToast('Payment total must match contract amount exactly', 'error');
        return;
      }
    }

    try {
      setGenerating(true);
      const response = await contractBuilderApi.generateContract({
        template_id: template.id,
        variables,
        contract_data: {
          title: formData.title,
          start_date: formData.start_date,
          end_date: formData.end_date,
          total_amount: parseFloat(formData.total_amount),
          payment_frequency: formData.payment_frequency,
          payment_day_of_month: formData.payment_frequency !== 'manual' 
            ? parseInt(formData.payment_day_of_month) 
            : null,
        },
        trainer_ids: selectedTrainerId ? [parseInt(selectedTrainerId)] : [],
        team_ids: selectedTeamId ? [parseInt(selectedTeamId)] : [],
        manual_payments: formData.payment_frequency === 'manual' 
          ? manualPayments.map(p => ({
              scheduled_date: p.scheduled_date,
              amount: parseFloat(p.amount),
              notes: p.notes || null
            }))
          : undefined
      });

      showToast('Contract generated successfully!', 'success');
      navigate(`/contracts/${response.data.id}`);
    } catch (error) {
      showToast('Failed to generate contract', 'error');
      console.error('Error generating contract:', error);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card>
          <div className="text-center py-12">
            <p className="text-gray-500">No active template found</p>
            <p className="text-sm text-gray-400 mt-2">
              Please create and activate a template first
            </p>
            <div className="mt-6">
              <Button onClick={() => navigate('/templates')}>
                Go to Templates
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <>
      {/* Contract Preview Modal */}
      <ContractPreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        contractHtml={previewHtml}
        title={`Preview: ${formData.title || 'Contract'}`}
      />
      
      <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="secondary"
          onClick={() => navigate('/contracts')}
          className="flex-shrink-0"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900">Generate Contract</h1>
          <p className="mt-1 text-sm text-gray-500">
            Using template: {template.name}
          </p>
        </div>
      </div>

      {/* Step 1: Trainer & Team Selection */}
      <Card>
        <Card.Header>
          <h2 className="text-lg font-semibold text-gray-900">Step 1: Select Trainer & Team</h2>
          <p className="text-sm text-gray-600">Optional: Auto-fill form data from trainer and team information</p>
        </Card.Header>
        <Card.Body>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input.Select
              label="Trainer"
              value={selectedTrainerId}
              onChange={(e) => setSelectedTrainerId(e.target.value)}
            >
              <option value="">Select a trainer...</option>
              {trainers.map(trainer => (
                <option key={trainer.id} value={trainer.id}>
                  {trainer.first_name} {trainer.last_name}
                </option>
              ))}
            </Input.Select>

            <Input.Select
              label="Team"
              value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value)}
            >
              <option value="">Select a team...</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>
                  {team.team_name}
                </option>
              ))}
            </Input.Select>
          </div>

          {selectedTrainerId && selectedTeamId && (
            <div className="mt-4">
              <Button
                size="sm"
                variant="secondary"
                onClick={handleAutoFill}
              >
                Auto-fill Form
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Step 2: Contract Metadata */}
      <Card>
        <Card.Header>
          <h2 className="text-lg font-semibold text-gray-900">Step 2: Contract Details</h2>
          <p className="text-sm text-gray-600">Basic contract information</p>
        </Card.Header>
        <Card.Body>
          <div className="space-y-4">
            <Input
              label="Contract Title"
              name="title"
              value={formData.title}
              onChange={handleFormChange}
              required
              placeholder="e.g., Head Coach Contract 2024"
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Start Date"
                name="start_date"
                type="date"
                value={formData.start_date}
                onChange={handleFormChange}
                required
              />

              <Input
                label="End Date"
                name="end_date"
                type="date"
                value={formData.end_date}
                onChange={handleFormChange}
                required
              />
            </div>

            <Input
              label="Total Amount (€)"
              name="total_amount"
              type="number"
              step="0.01"
              value={formData.total_amount}
              onChange={handleFormChange}
              required
              placeholder="5000"
            />
          </div>
        </Card.Body>
      </Card>

      {/* Step 3: Payment Schedule */}
      <Card>
        <Card.Header>
          <h2 className="text-lg font-semibold text-gray-900">Step 3: Payment Schedule</h2>
          <p className="text-sm text-gray-600">Configure how payments will be scheduled</p>
        </Card.Header>
        <Card.Body>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input.Select
                label="Payment Frequency"
                name="payment_frequency"
                value={formData.payment_frequency}
                onChange={handleFormChange}
                required
              >
                <option value="one-time">One-time (upon contract start)</option>
                <option value="twice">Twice per contract period</option>
                <option value="twice-yearly">Twice Yearly (May & December)</option>
                <option value="quarterly">Quarterly (4 payments if ≤1yr, else calendar quarters)</option>
                <option value="monthly">Monthly</option>
                <option value="manual">Manual Payment Schedule</option>
              </Input.Select>

              {formData.payment_frequency !== 'manual' && (
                <Input
                  label="Payment Day of Month"
                  name="payment_day_of_month"
                  type="number"
                  min="1"
                  max="31"
                  value={formData.payment_day_of_month}
                  onChange={handleFormChange}
                  helpText="Day of month for recurring payments (1-31)"
                />
              )}
            </div>

            {/* Payment Schedule Preview or Manual Editor */}
            {formData.start_date && formData.end_date && formData.total_amount ? (
              formData.payment_frequency === 'manual' ? (
                <ManualPaymentScheduleEditor
                  totalAmount={parseFloat(formData.total_amount) || 0}
                  payments={manualPayments}
                  onChange={setManualPayments}
                  startDate={formData.start_date}
                  endDate={formData.end_date}
                />
              ) : (
                <PaymentSchedulePreview
                  payments={calculatePayments(formData)}
                  isPreview={true}
                />
              )
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                <p className="text-gray-500">
                  Complete Step 2 (contract details) to preview payment schedule
                </p>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Step 4: Template Variables */}
      <Card>
        <Card.Header>
          <h2 className="text-lg font-semibold text-gray-900">Step 4: Additional Information</h2>
          <p className="text-sm text-gray-600">Complete any remaining fields (auto-fill recommended)</p>
        </Card.Header>
        <Card.Body>
          {(() => {
            // Filter to show only user-editable fields (hide auto-filled and signature fields)
            // Also hide fields that have been auto-filled dynamically
            const visibleFields = (template.template_variables || [])
              .filter(variable => {
                // Always hide predefined hidden fields
                if (HIDDEN_FIELDS.includes(variable.name)) {
                  return false;
                }
                
                // Also hide fields that were auto-filled and have values
                if (autoFilledFields.has(variable.name) && variables[variable.name]) {
                  return false;
                }
                
                return true;
              });
            
            if (visibleFields.length === 0) {
              // All fields are auto-filled - show success message
              return (
                <div className="p-6 bg-green-50 border border-green-200 rounded-lg text-center">
                  <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">All Set!</h3>
                  <p className="text-gray-600">
                    All template fields have been automatically filled. You can preview or generate your contract.
                  </p>
                </div>
              );
            }
            
            return (
              <>
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-gray-700 mb-1">
                    {visibleFields.length} field{visibleFields.length !== 1 ? 's' : ''} require{visibleFields.length === 1 ? 's' : ''} your attention
                  </h3>
                  <p className="text-xs text-gray-500">
                    {selectedTrainerId && selectedTeamId
                      ? 'Fill manually or use the Auto-fill button in Step 1'
                      : 'Fill manually or select a trainer and team in Step 1 to use Auto-fill'}
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {visibleFields.map(variable => {
                    return (
                      <div key={variable.name}>
                        <Input
                          label={variable.label}
                          type={variable.type || 'text'}
                          value={variables[variable.name] || ''}
                          onChange={(e) => handleVariableChange(variable.name, e.target.value)}
                          required={variable.required}
                          placeholder={`Enter ${variable.label.toLowerCase()}`}
                          helpText={variable.required ? 'Required field' : null}
                        />
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </Card.Body>
      </Card>
      
      {/* Warning Card if no signature */}
      {!hasUserSignature && (
        <Card className="bg-amber-50 border-amber-200">
          <div className="flex items-start gap-3 text-sm text-amber-800 p-4">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold mb-1">Signature Required</h4>
              <p>
                You have not set up your signature yet. Please add your signature in{' '}
                <button
                  onClick={() => navigate('/settings/signatures')}
                  className="underline font-medium hover:text-amber-900"
                >
                  Profile Settings
                </button>
                {' '}before generating contracts.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <div className="text-sm text-blue-800">
          <h4 className="font-semibold mb-2">Contract Generation Workflow:</h4>
          <ul className="list-disc list-inside space-y-1">
            <li>Step 1: Select trainer & team (optional but recommended for auto-fill)</li>
            <li>Step 2: Enter contract details (title, dates, amount)</li>
            <li>Step 3: Configure payment schedule</li>
            <li>Step 4: Complete any remaining fields (or use Auto-fill)</li>
            <li>Preview before generating to verify all information</li>
            <li>Creator signature applied automatically (must be set in profile)</li>
            <li>Trainer signs later via signing link</li>
          </ul>
        </div>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-between items-center">
        <Button
          variant="secondary"
          onClick={handlePreview}
          disabled={!formData.title}
        >
          Preview Contract
        </Button>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => navigate('/contracts')}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            loading={generating}
          >
            Generate Contract
          </Button>
        </div>
      </div>
    </div>
    </>
  );
}